import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Select } from '../../../Select';

import classNames from 'classnames';
import classes from './Heading.module.scss';

interface HeadingProps {
  editor: Editor;
}

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export const Heading: FunctionComponent<HeadingProps> = ({ editor }) => {
  const { t } = useTranslation();

  const { isActiveH1, isActiveH2, isActiveH3, isActiveH4, isActiveH5, isActiveH6 } = useEditorState(
    {
      editor,
      selector: ({ editor }) => ({
        isActiveH1: editor.isActive('heading', { level: 1 }),
        isActiveH2: editor.isActive('heading', { level: 2 }),
        isActiveH3: editor.isActive('heading', { level: 3 }),
        isActiveH4: editor.isActive('heading', { level: 4 }),
        isActiveH5: editor.isActive('heading', { level: 5 }),
        isActiveH6: editor.isActive('heading', { level: 6 }),
      }),
    }
  );

  const items = useMemo(
    () => [
      { id: 1, title: t('richTextEditor.tools-header-heading-level', { level: 1 }) },
      { id: 2, title: t('richTextEditor.tools-header-heading-level', { level: 2 }) },
      { id: 3, title: t('richTextEditor.tools-header-heading-level', { level: 3 }) },
      { id: 4, title: t('richTextEditor.tools-header-heading-level', { level: 4 }) },
      { id: 5, title: t('richTextEditor.tools-header-heading-level', { level: 5 }) },
      { id: 6, title: t('richTextEditor.tools-header-heading-level', { level: 6 }) },
    ],
    [t]
  );

  const selectedItemId = isActiveH1
    ? 1
    : isActiveH2
    ? 2
    : isActiveH3
    ? 3
    : isActiveH4
    ? 4
    : isActiveH5
    ? 5
    : isActiveH6
    ? 6
    : undefined;

  const onChange = useCallback(
    (level: HeadingLevel) => editor.chain().focus().toggleHeading({ level }).run(),
    [editor]
  );

  const allowed = useMemo(
    () =>
      ['paragraph', 'heading', 'taskList', 'orderedList', 'bulletList'].includes(
        editor.state.selection.$from.node(1)?.type.name
      ),
    [editor.state.selection]
  );

  return (
    <Select
      className={classNames(classes['heading'], { [classes['heading--mobile']]: isMobileOnly })}
      placeholderLabel={t('richTextEditor.header-config-placeholder')}
      selectedItemId={selectedItemId}
      active={Boolean(selectedItemId)}
      items={items}
      onChange={(level) => onChange(level as HeadingLevel)}
      disabled={!allowed}
      {...(isMobileOnly && { compactSidePadding: true, listMinWidthRem: 10.5 })}
      noBorder
    />
  );
};
